/**
 * **濒死那一瞬间的"打击"** —— 所有冲击通道的**时间表**（纯函数 + 一个不碰 Pixi 的类）。
 *
 * 评审：
 *
 * > 抽到 [异常] 牌、濒死特效出现那一刻还不够 IMPACT，应当看起来更加有冲击力和
 * > 威胁性。除了还能再加一个屏幕震动外我已经没招了。
 *
 * 诊断见 `docs/dying-impact.md` §1：原来所有开关都挂在"飞抵中点"（`t=0`），
 * 而牌"扑到脸上"要到 `t≈420ms` 才完成（`dyingPop` 先缩到 0.82 再 `t^4` 撑到 2 倍）
 * —— 最强的那个故障 ×10（0.25s 衰减）在牌还只有 0.82 倍时就用完了，
 * 等它撑满那一帧反而什么都没发生。**打击点被抹平了。**
 *
 * ## 一次打击的形状
 *
 * ```
 *            hit()
 *              │
 *   闪 ▏      ██
 *     ▏     █   █
 *     ▏___█       █___
 *              │
 *   冻 ▏███████          ← freezeMs：整幅画面完全不动
 *   世 ▏████████████     ← ambientHoldMs：背景/横带/裂缝/呼吸继续不动
 *              │
 *   震 ▏█▆▄▂▁            ← trauma² 驱动，每秒 decay
 * ```
 *
 * 为什么要有"冻"：`docs/dying-impact.md` §2 —— 业界把"冻结帧（hit stop）"当作
 * 最强的"重"信号；眼睛需要**停一下**才看得见那一下。⚠️ 冻结用**真实时间**计时
 * （`update()` 照收 `deltaMS`），否则永远不恢复（业界那个
 * "时间倍率 0 时 WaitForSeconds 永远不满足"的坑）。
 *
 * 为什么要有"世界静止"：威胁感来自**运动变少**，不是变多。打击之后让背景、
 * 横带、裂缝、呼吸一起停 ~0.2 秒（牌继续撑满），然后世界才"崩开"。
 *
 * ⚠️ 这个文件**不认识 Pixi**：它只回答"这一帧每个通道该是多少"。
 * 把数写进画面的是 `screenHost.ts`（`world` 变换 + `ImpactLayer` 那两层叠加），
 * 所以时间表能在 node 里断言（`test/render/impact.test.ts`）。
 */

import { LIGHT, PRESS } from './palette';

// ---------------------------------------------------------------- 参数 ----

export interface ImpactStyle {
  // ---- S2 冻结 ----
  /** 主打击时整幅画面完全定住多久（毫秒） */
  freezeMs: number;
  /**
   * 打击之后**背景**（标量场 / 横带 / 裂缝 / 呼吸）继续定住多久
   * （毫秒，从打击算起，必须 ≥ `freezeMs`）。牌不在此列 —— 它还在撑满。
   */
  ambientHoldMs: number;

  // ---- S3 冲击帧 ----
  /** 白闪峰值透明度（**不是 1**：全白读起来像渲染坏了） */
  flashPeak: number;
  /**
   * 从峰值退回 0 用多久（毫秒）。
   *
   * ⚠️ **没有"冲上去"的那一段**：打击那一帧就是峰值。原来有个 `flashUpMs`
   *    （16ms 里从 0 涨到峰值），结果它踩了一个很隐蔽的坑 —— 冻结帧把
   *    **包络自己也冻住了**（见 `update()`），于是那 16ms 永远走不完：
   *    整段冻结里闪一直是 0，白闪**一次都没亮**（`smoke:beat` 抓到的）。
   *    冲击帧本来就该"啪"地一下，不需要上升沿。
   */
  flashDownMs: number;
  /** 闪光颜色（纸白 —— 这一版的设计语言里"闪"就是纸白） */
  flashColor: number;
  /**
   * **反相冲击帧**的峰值（0 = 不做）。评审：颜色只要够轻微、不影响可读性就能接受。
   *
   * ⚠️ 它和"故障滤镜不改变颜色"那条要求**冲突**（评审为这一档专门放了行）。
   *    1 帧的反相是动画里那一招（`docs/dying-impact.md` §4.3 B3）。
   */
  invertPeak: number;
  /** 反相持续多久（毫秒）。一个帧左右就够，别拉长 */
  invertMs: number;

  // ---- S4 震动（trauma）----
  /** 主打击加多少 trauma（0..1） */
  hitTrauma: number;
  /** 落地那一记小 kick 加多少 trauma */
  landTrauma: number;
  /** trauma 每秒衰减多少 */
  traumaDecay: number;
  /**
   * trauma = 1 时的最大位移（逻辑像素，左右 / 上下）。
   *
   * ⚠️ **只有平移，没有旋转**（评审要的"屏幕震动"这一条里最容易被做坏的就是它）：
   *    旋转要求 `world` 有 `pivot`，而背景的四边形是拿 `viewportOf()` 从
   *    `world.scale/position` 反推的（`background.ts`），它不认识 pivot ——
   *    一转，场就铺不满视口，横带也铺不到屏幕两边（`smoke:beat` ⑦ 盯着这条）。
   *    6–9Hz 的位移已经够猛了，不必为了旋转把整条背景管线拖下水。
   */
  shakeX: number;
  shakeY: number;

  // ---- S5 推镜 ----
  /** 主打击的推镜倍率（`world.scale` 乘它）。1.03 = 推近 3% */
  hitZoom: number;
  /** 推镜弹回去用多久（毫秒） */
  hitZoomMs: number;
  /** 落地那一记小 kick 的倍率（比主打击小得多：那是"牌砸在桌上"） */
  landZoom: number;
  /** 落地 kick 弹回去用多久（毫秒） */
  landZoomMs: number;

  /**
   * **打击之前那一下"吸气"**：落地之后、撑满之前，整块画面**往回收一点**。
   *
   * 收回来的那一下是预备动作（和 `dyingPop` 的先缩后撑是同一件事，只不过
   * 这个作用在**整个世界**上：牌缩 + 画面缩 = 吸气，然后打击那一下才炸得开）。
   * 1 = 不吸气。默认 `0.965`（收回 3.5%）。
   */
  windupZoom: number;
  /**
   * 吸气多久（毫秒）。**和 `drawReveal.ts` 的 `HOLD_MS` 是一对**：
   * 那一段就是"落地 → 撑满"的时长，吸气正好在撑满那一刻最紧，然后被打击打断。
   */
  windupMs: number;

  // ---- A3 光圈收束 / 暗角 ----
  /** 常态暗角浓度（濒死期间一直在，压住画面四角） */
  vignetteBase: number;
  /** 打击那一瞬间收到多紧 */
  vignetteHit: number;
  /** 收束到常态用多久（毫秒） */
  vignetteMs: number;
  /** 常态暗角跟着呼吸摆动多少（和辉光同一个 2.4s 周期） */
  vignetteBreath: number;

  // ---- A5 边缘红脉冲 ----
  /** 每来一小节，边缘脉冲加多少 */
  edgeGain: number;
  /** 边缘脉冲每秒衰减多少 */
  edgeDecay: number;

  // ---- B1/B2 颜色 ----
  /** 打击时全局去色多少（0..1）。⚠️ 偏红的地方会被"加"回来，见 `glitch.ts` */
  desat: number;
  /** 色差的峰值偏移（**物理像素**） */
  aberrPx: number;
  /** 去色 / 色差回到常态用多久（毫秒）。默认和故障滤镜的 onset 同一量级 */
  colorMs: number;

  // ---- IMPACT 的"渐强"（音量增益，`AudioSink.setBgmBoost`）----
  /**
   * **从"抽牌触发濒死"那一刻开始，BGM 的音量往上爬多久**（毫秒）。
   *
   * 评审：「IMPACT 时要有逐渐上升的音量增益，从回合结束抽牌触发濒死那一刻开始
   * 上升，IMPACT 瞬间达到最大，然后快速衰减」。
   *
   * 所以它由对局屏在**建那一段致命牌动画时** `anticipate()` 一下开始计时；
   * 爬到一半就被 `hit()` 打断也没关系 —— 打击那一刻直接**顶到最大**。
   */
  riserMs: number;
  /** 顶到最大时比原音量响多少（0.45 = 响 45%）。⚠️ 只加不剪，不碰无缝循环 */
  riserGain: number;
  /** 打击之后多久落回原音量（毫秒）。要"快速衰减" */
  riserFallMs: number;

  // ---- BGM 的"播放故障"滤镜 + 混响（`audio/fx.ts`）----
  /**
   * 故障滤镜从最大衰减到 0 用多久（毫秒）。
   *
   * 评审："自 IMPACT 时刻出现且达到最大，随后衰减消失" —— 这里的形状就是
   * 那件事：`sinceHitMs = 0` 时是 1（最大），然后 `1 - t³` 掉下去。
   * ⚠️ 它比画面那几项**长得多**（默认 900ms）：混响的尾巴本来就长，
   *    而且"信号坏了"这件事不该在一两帧里就修好。
   */
  fxMs: number;

  // ---- B4 对齐到 beat ----
  /**
   * 主打击要不要**等到下一拍**再发生（`docs/dying-impact.md` §4.3 B4）。
   *
   * ⚠️ 默认**关**：它会让"抽到牌"和"看得见的反应"之间最多多等一拍，
   *    和"抽到牌立刻有反应"直接冲突。开着是给评审对比用的。
   */
  beatAlign: boolean;
}

export const DEFAULT_IMPACT_STYLE: ImpactStyle = {
  // ★ 评审第三轮："冲击性是够了，但是太慢了，要更快的完成"
  //    —— 幅度一个都没降，只把时间收短：冻结 140→100、世界静止 300→140
  freezeMs: 100,
  ambientHoldMs: 140,

  // ★ 评审："参数都给得太小太保守，不够快不够炸裂" —— 这一组是扈开了调的
  flashPeak: 0.85,
  flashDownMs: 80,
  flashColor: LIGHT.base,
  // 一帧多的**全反相**：动画里那一招（评审：颜色只要够轻微、不影响可读性就能接受）
  invertPeak: 1,
  invertMs: 30,

  hitTrauma: 0.85,
  landTrauma: 0.3,
  // ★ 整段更快：震得更猛也收得更快（拖着不放就是"拖沓"）
  traumaDecay: 1.6,
  shakeX: 34,
  shakeY: 24,

  windupZoom: 0.965,
  // 和 `drawReveal.ts` 的 `HOLD_MS` **一对**（那一段就是"落地 → 撑满"）
  windupMs: 220,

  hitZoom: 1.075,
  hitZoomMs: 140,
  landZoom: 1.02,
  landZoomMs: 90,

  vignetteBase: 0.5,
  vignetteHit: 0.92,
  vignetteMs: 170,
  vignetteBreath: 0.1,

  edgeGain: 0.5,
  edgeDecay: 7,

  desat: 0.8,
  aberrPx: 7,
  colorMs: 320,

  beatAlign: false,

  fxMs: 900,

  riserMs: 900,
  riserGain: 0.45,
  riserFallMs: 220,
};

// ---------------------------------------------------------------- 包络 ----

/**
 * **"一上来就是峰值，然后慢慢退"** —— `1 - t³`。
 *
 * ⚠️ 用它而不是 `ease-out`（`1-(1-t)³`）：那一条是 0→1 的**上升**曲线，
 *    峰值在末尾；冲击要的正好相反 —— 第 0 帧就是峰值，然后**先快后慢**地
 *    退掉（前 1/3 时间退掉 96%，剩下的都是余辉）。
 */
function decayCurve(t: number): number {
  const u = Math.max(0, Math.min(1, t));
  return 1 - u * u * u;
}

/**
 * **白闪的包络。** 打击那一帧就是峰值（`sinceMs = 0`），然后按 `flashDownMs`
 * 退掉 —— 曲线是 `1 - t³`：前三分之一就退掉九成六，剩下的是余辉。
 *
 * ⚠️ 冻结帧期间包络**不走**（`update()` 直接返回），所以"峰值"会**定格**
 *    在画面上 —— 这正是"冲击帧"要的样子。
 */
export function flashEnvelope(sinceMs: number, style: ImpactStyle = DEFAULT_IMPACT_STYLE): number {
  if (sinceMs < 0 || sinceMs >= style.flashDownMs) return 0;
  return decayCurve(sinceMs / Math.max(1, style.flashDownMs)) * style.flashPeak;
}

/** **反相冲击帧**：一帧冲到峰值，然后按 `invertMs` 线性的退掉 */
export function invertEnvelope(sinceMs: number, style: ImpactStyle = DEFAULT_IMPACT_STYLE): number {
  if (sinceMs < 0 || style.invertPeak <= 0) return 0;
  if (sinceMs >= style.invertMs) return 0;
  return style.invertPeak * (1 - sinceMs / Math.max(1, style.invertMs));
}

/**
 * **去色 / 色差**的包络：打击那一刻全开，然后指数回到常态。
 *
 * ⚠️ 它比白闪长得多（默认 420ms）：这两个是"世界在这一下之后变成另一副样子"
 * 的余韵，不是那一帧的闪。用指数是为了"永远不精确到 0 但很快看不出来"。
 */
export function colorEnvelope(sinceMs: number, style: ImpactStyle = DEFAULT_IMPACT_STYLE): number {
  if (sinceMs < 0) return 0;
  return Math.exp(-sinceMs / Math.max(1, style.colorMs));
}

/**
 * **trauma → 位移**（`docs/dying-impact.md` §2.2）。
 *
 * `shake = trauma²`：小打击几乎不动、大打击猛地一下 —— 线性的话"轻"和"重"
 * 只差一个系数，读起来都差不多。位移用 `sin` 采样（**不是每帧随机数**：
 * 那是嗡嗡的静电，不是震动）。
 */
export function traumaShake(
  trauma: number,
  seconds: number,
  style: ImpactStyle = DEFAULT_IMPACT_STYLE,
): { x: number; y: number } {
  const t = Math.max(0, Math.min(1, trauma));
  const amount = t * t;
  if (amount <= 0) return { x: 0, y: 0 };
  return {
    // ⚠️ 6–9 Hz：这是"猛烈地抖"，不是"慢慢地晃"。低于 4Hz 读起来像镜头在漂
    x: style.shakeX * amount * Math.sin(seconds * 41.0),
    y: style.shakeY * amount * Math.sin(seconds * 53.0 + 1.3),
  };
}

/**
 * **打击之前那一下"吸气"**：落地之后、撑满之前，世界慢慢往回收。
 *
 * 形状是 smoothstep（慢慢吸、末段顶住）—— **不是** `decayCurve`：吸气要的是
 * "越收越紧"，一下收到位反而像是画面自己缩了。已经打了（`sinceHitMs ≥ 0`）
 * 或还没落地时都是 1。
 */
export function windupZoomAt(
  sinceLandMs: number,
  sinceHitMs: number,
  style: ImpactStyle = DEFAULT_IMPACT_STYLE,
): number {
  if (sinceLandMs < 0 || sinceHitMs >= 0) return 1;
  const t = Math.max(0, Math.min(1, sinceLandMs / Math.max(1, style.windupMs)));
  const pull = t * t * (3 - 2 * t);
  return 1 - (1 - style.windupZoom) * pull;
}

/** 一个"猛地推近再弹回"的倍率：`1 + (peak - 1) × ease-out` */
export function punchZoom(sinceMs: number, peak: number, ms: number): number {
  if (sinceMs < 0 || sinceMs >= ms) return 1;
  return 1 + (peak - 1) * decayCurve(sinceMs / Math.max(1, ms));
}

/** 暗角：常态 + 打击那一下收得更紧 + 濒死期间跟着呼吸摆动 */
export function vignetteAt(
  sinceHitMs: number,
  breathSeconds: number,
  style: ImpactStyle = DEFAULT_IMPACT_STYLE,
): number {
  const bump = sinceHitMs < 0 ? 0 : decayCurve(sinceHitMs / Math.max(1, style.vignetteMs));
  const breath = Math.sin((breathSeconds / BREATH_SECONDS) * Math.PI * 2) * 0.5 + 0.5;
  const base = style.vignetteBase + style.vignetteBreath * breath;
  return base + (style.vignetteHit - style.vignetteBase) * bump;
}

/** 濒死层那口呼吸的周期（秒）。暗角跟着它走，两个数不能各算一份 */
export const BREATH_SECONDS = 2.4;

/**
 * **渐强（riser）**：从被 `anticipate()` 之后开始往上爬，打击那一刻顶到 1，
 * 然后按 `riserFallMs` **快速**落回 0。
 *
 * ⚠️ 没被 arm（`armed = false`）时永远是 0 —— 平时 BGM 一个字节都不动。
 */
export function riserEnvelope(
  armed: boolean,
  sinceArmMs: number,
  sinceHitMs: number,
  style: ImpactStyle = DEFAULT_IMPACT_STYLE,
): number {
  if (!armed) return 0;
  if (sinceHitMs >= 0) {
    // 已经打了：从顶上一路落回 0（"快速衰减"）
    if (sinceHitMs >= style.riserFallMs) return 0;
    return decayCurve(sinceHitMs / Math.max(1, style.riserFallMs));
  }
  // 还在爬：`t²`（越来越快，最后一下正好顶到最大）
  const t = Math.max(0, Math.min(1, sinceArmMs / Math.max(1, style.riserMs)));
  return t * t;
}

/**
 * **BGM 的"播放故障"剂量**：打击那一刻 1（最大），然后按 `fxMs` 衰减回 0。
 *
 * 和别的包络一样是纯函数 —— 它推给音频那一侧的只有**一个数**
 * （`AudioSink.setBgmFx` → `audio/fx.ts` 里那串降位深/失真/混响的剂量）。
 */
export function fxEnvelope(sinceMs: number, style: ImpactStyle = DEFAULT_IMPACT_STYLE): number {
  if (sinceMs < 0) return 0;
  if (sinceMs >= style.fxMs) return 0;
  return decayCurve(sinceMs / Math.max(1, style.fxMs));
}

// ---------------------------------------------------------------- 通道 ----

/** 这一帧每个通道该是多少。`screenHost` 原样写进画面 */
export interface ImpactChannels {
  /** 整幅画面完全不动（这一帧**不推进任何动画**） */
  readonly frozen: boolean;
  /** 背景/横带/裂缝/呼吸还要定住一会儿（牌可以动） */
  readonly ambientHeld: boolean;
  /** 从主打击过了多久（毫秒）。没打过就是 `-1` */
  readonly sinceHitMs: number;
  readonly flash: number;
  readonly flashColor: number;
  readonly invert: number;
  readonly desat: number;
  readonly aberr: number;
  readonly shake: { readonly x: number; readonly y: number };
  readonly zoom: number;
  readonly vignette: number;
  readonly edge: number;
  /** BGM 的"播放故障"剂量（0..1） */
  readonly fx: number;
  /**
   * **BGM 的音量增益倍数**（1 = 不动，> 1 = 正在渐强）。
   *
   * ⚠️ 它是**乘**在设置音量上的：`port.setVolume(volume × boost)` ——
   *    评审要的是"逐渐上升"，不是"把音乐掐掉"（那一条第三轮已经删了）。
   */
  readonly gain: number;
}

const IDLE: ImpactChannels = Object.freeze({
  frozen: false,
  ambientHeld: false,
  sinceHitMs: -1,
  flash: 0,
  flashColor: 0xffffff,
  invert: 0,
  desat: 0,
  aberr: 0,
  shake: Object.freeze({ x: 0, y: 0 }),
  zoom: 1,
  vignette: 0,
  edge: 0,
  fx: 0,
  gain: 1,
});

/**
 * **打击的时钟。**
 *
 * 用法（`screenHost`）：
 *
 * ```ts
 *   impact.update(ticker.deltaMS / 1000, energy?.isDying === true);
 *   const ch = impact.channels();
 *   applyWorldTransform(world, ch);       // ★ 先摆世界变换，背景的四边形才跟着震
 *   if (!ch.frozen) { ...背景 / 故障 / 屏幕每帧... }
 *   impactLayer.render(ch);
 * ```
 */
export class Impact {
  #style: ImpactStyle;
  #trauma = 0;
  #edge = 0;
  /** 从主打击过了多久（毫秒）。`Number.POSITIVE_INFINITY` = 这一轮还没打过 */
  #sinceHitMs = Number.POSITIVE_INFINITY;
  /** 从落地 kick 过了多久（毫秒） */
  #sinceLandMs = Number.POSITIVE_INFINITY;
  #freezeLeftMs = 0;
  #ambientLeftMs = 0;
  /** 震动用的时间轴（秒）—— 只在非冻结时前进，于是冻住的位移是"定格"的 */
  #shakeSeconds = 0;
  /** 濒死开始到现在（秒），暗角的呼吸跟着它 */
  #breathSeconds = 0;
  /** 有一记在等下一小节（`beatAlign`），见 `hit()` */
  #pending = false;
  /** 渐强被 arm 过没有（见 `anticipate()`） */
  #armed = false;
  /** 从 arm 到现在（毫秒） */
  #sinceArmMs = 0;

  constructor(style: Partial<ImpactStyle> = {}) {
    this.#style = { ...DEFAULT_IMPACT_STYLE, ...style };
  }

  get style(): Readonly<ImpactStyle> {
    return this.#style;
  }

  applyStyle(style: Partial<ImpactStyle>): void {
    this.#style = { ...this.#style, ...style };
  }

  /** 飞到中点、牌砸在桌上：**一记小 kick**（不要和主打击搞成同一下） */
  land(): void {
    this.#sinceLandMs = 0;
    this.#trauma = Math.min(1, this.#trauma + this.#style.landTrauma);
  }

  /**
   * ★ **主打击**：牌撑到 2 倍那一帧。
   *
   * 一次打击 = 冻结 + 白闪 + 反相 + 震动 + 推镜 + 去色/色差，
   * 全部挂在**同一个时刻**上 —— 这是"IMPACT"这个词在这里的全部含义。
   */
  hit(): void {
    if (this.#style.beatAlign) {
      this.#pending = true;
      return;
    }
    this.#fire();
  }

  /**
   * ★ **开始渐强**：对局屏在"抽牌触发濒死"那一刻调一次。
   *
   * 从这一刻起音量增益往上爬，`hit()` 那一刻顶到最大，然后快速落回。
   * 重复调用会重新计时（同一段动画里只该 arm 一次）。
   */
  anticipate(): void {
    this.#armed = true;
    this.#sinceArmMs = 0;
  }

  /** 现在在渐强吗（读数用） */
  get armed(): boolean {
    return this.#armed;
  }

  /** 音乐走了一小节 → 边缘红脉冲推一下（顺带把 `beatAlign` 那一记打出去） */
  bar(): void {
    this.#edge = Math.min(1, this.#edge + this.#style.edgeGain);
    if (this.#pending) {
      this.#pending = false;
      this.#fire();
    }
  }

  /** 真的打一记（`hit()` 与 `bar()` 都走这里） */
  #fire(): void {
    this.#sinceHitMs = 0;
    this.#freezeLeftMs = this.#style.freezeMs;
    this.#ambientLeftMs = Math.max(this.#style.ambientHoldMs, this.#style.freezeMs);
    this.#trauma = Math.min(1, this.#trauma + this.#style.hitTrauma);
  }

  /** 有一记在等下一小节吗（`beatAlign` 开着时才有意义，读数用） */
  get pendingHit(): boolean {
    return this.#pending;
  }

  /**
   * 推进一帧。
   *
   * ⚠️ **冻结期间只减冻结计时**，别的通道一律不动 —— 于是"这一帧"和"上一帧"
   *    逐位相同，画面真的是定格的（不是"掉帧"，冒烟量得出来）。
   *
   * @param dtSeconds 真实经过的秒数（`ticker.deltaMS / 1000`）
   * @param dying 现在在不在濒死（暗角的呼吸只在濒死期间走）
   */
  update(dtSeconds: number, dying = false): void {
    const dtMs = Math.max(0, dtSeconds) * 1000;
    if (this.frozen) {
      this.#freezeLeftMs -= dtMs;
      this.#ambientLeftMs -= dtMs;
      return;
    }
    this.#freezeLeftMs = Math.max(0, this.#freezeLeftMs - dtMs);
    this.#ambientLeftMs = Math.max(0, this.#ambientLeftMs - dtMs);
    this.#sinceHitMs += dtMs;
    this.#sinceLandMs += dtMs;
    this.#shakeSeconds += Math.max(0, dtSeconds);
    this.#sinceArmMs += dtMs;
    if (dying) this.#breathSeconds += Math.max(0, dtSeconds);
    this.#trauma = Math.max(0, this.#trauma - this.#style.traumaDecay * Math.max(0, dtSeconds));
    this.#edge = Math.max(0, this.#edge - this.#style.edgeDecay * Math.max(0, dtSeconds));
  }

  /** 这一帧画面完全不动 */
  get frozen(): boolean {
    return this.#freezeLeftMs > 0;
  }

  /** 背景等环境层还要定住（牌不在此列） */
  get ambientHeld(): boolean {
    return this.#ambientLeftMs > 0;
  }

  /** 冻结还剩多少毫秒（读数用）。冒烟靠它判断"这一帧该不该动" */
  get freezeLeftMs(): number {
    return Math.max(0, this.#freezeLeftMs);
  }

  /** 世界静止还剩多少毫秒（读数用） */
  get ambientLeftMs(): number {
    return Math.max(0, this.#ambientLeftMs);
  }

  /** 这一记打击打了多久了（毫秒）。`-1` = 这一轮还没打过 */
  get sinceHitMs(): number {
    return Number.isFinite(this.#sinceHitMs) ? this.#sinceHitMs : -1;
  }

  /** 现在这一记打击的 trauma（读数用） */
  get trauma(): number {
    return this.#trauma;
  }

  /** 边缘脉冲现在多亮（读数用） */
  get edge(): number {
    return this.#edge;
  }

  /** 把状态清回"什么都没发生过"（离开对局 / 重置时） */
  reset(): void {
    this.#trauma = 0;
    this.#edge = 0;
    this.#sinceHitMs = Number.POSITIVE_INFINITY;
    this.#sinceLandMs = Number.POSITIVE_INFINITY;
    this.#freezeLeftMs = 0;
    this.#ambientLeftMs = 0;
    this.#shakeSeconds = 0;
    this.#breathSeconds = 0;
    this.#pending = false;
    this.#armed = false;
    this.#sinceArmMs = 0;
  }

  /**
   * 这一帧每个通道的值。**纯读**，不改状态 —— 于是它可以在渲染前问一次、
   * 调试读数里再问一次，两次一样。
   */
  channels(dying = false): ImpactChannels {
    const style = this.#style;
    const sinceHitMs = Number.isFinite(this.#sinceHitMs) ? this.#sinceHitMs : -1;
    if (sinceHitMs < 0 && this.#trauma <= 0 && !(dying && this.#edge > 0) && !dying && !this.#armed) {
      // 什么都没发生过、也不在濒死：一个数都不用算
      return IDLE;
    }
    const sinceLandMs = Number.isFinite(this.#sinceLandMs) ? this.#sinceLandMs : -1;
    const color = sinceHitMs < 0 ? 0 : colorEnvelope(sinceHitMs, style);
    // ★ 拜击之前先"吸气"（整块画面往回收），然后才是那一下推近
    const zoom =
      windupZoomAt(sinceLandMs, sinceHitMs, style) *
      Math.max(
        punchZoom(sinceLandMs, style.landZoom, style.landZoomMs),
        punchZoom(sinceHitMs, style.hitZoom, style.hitZoomMs),
      );
    return {
      frozen: this.frozen,
      ambientHeld: this.ambientHeld,
      sinceHitMs,
      flash: flashEnvelope(sinceHitMs, style),
      flashColor: style.flashColor,
      invert: invertEnvelope(sinceHitMs, style),
      desat: style.desat * color,
      aberr: style.aberrPx * color,
      shake: traumaShake(this.#trauma, this.#shakeSeconds, style),
      zoom,
      vignette: dying ? vignetteAt(sinceHitMs, this.#breathSeconds, style) : 0,
      /**
       * ⚠️ 边缘脉冲**只在濒死期间**有效（评审报的"有些 Bar 事件没做濒死检查，常态生效"）。
       *    叫做的地方已经加了闸门，这里再把通道也掩一道 ——
       *    两处都在算一样的东西，但"忘了判濒死"的后果是玩家每小节看一次红光，值得两道。
       */
      edge: dying ? this.#edge : 0,
      fx: fxEnvelope(sinceHitMs, style),
      gain: 1 + style.riserGain * riserEnvelope(this.#armed, this.#sinceArmMs, sinceHitMs, style),
    };
  }
}


/** 边缘脉冲的颜色（印刷红 —— 和濒死那一族用同一个红） */
export const IMPACT_EDGE_COLOR = PRESS.red;
